import React from 'react'
import { Box, DialogContent, Typography } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { StorageSystem, Storage } from 'src/Libs/flow/installation/forms'
import { getStorageSettingsToRestore } from 'src/Libs/flow/installation/api/save-installation/save-storage-settings'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TabButtonGroup, TextField } from 'src/Libs/inputs/react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useDialogActionsPositioned } from '../../../hooks'
import Actions from '@e3dc-react/shell/Elements/Dialog/ActionDialog/Actions'

type SettingsOption = 'use default settings' | 'restore previous settings'

const useDefaultsOption: SettingsOption = 'use default settings'
const restorePreviousOption: SettingsOption = 'restore previous settings'

type StorageForm = {
  storageName: string
  restorePreviousSettings: SettingsOption
}

interface AddStorageProps {
  installationId: number | null
  error: string | null
  closeDialog: () => void
  storages: Storage[]
  addStorage: (name: string, previousStorageSystem: StorageSystem | null) => void
}

/**
 * Form to add a storage to the installation. Thought of as content inside a dialog.
 */
export const AddStorage: React.FC<AddStorageProps> = ({ installationId, error, closeDialog, storages, addStorage }) => {
  const { t } = useCustomTranslation()
  const methods = useForm<StorageForm>({
    mode: 'onChange',
    defaultValues: {
      storageName: t('installation.types.storage'),
      restorePreviousSettings: useDefaultsOption,
    },
    resolver: zodResolver(validationSchema),
  })
  const { handleSubmit, formState } = methods

  const storageSystemToRestore = getStorageSettingsToRestore(installationId)
  const hasStorageSystemToRestore = !!storageSystemToRestore

  const onSubmit: SubmitHandler<StorageForm> = data => {
    const storageSystem = data.restorePreviousSettings === 'restore previous settings' ? storageSystemToRestore : null
    addStorage(data.storageName, storageSystem)
    closeDialog()
  }

  const isSaveButtonEnabled = !error && formState.isValid
  const actions = useDialogActionsPositioned(t('installation.productEditor.edit.storage'), handleSubmit(onSubmit), !isSaveButtonEnabled, closeDialog)

  return (
    <FormProvider {...methods}>
      <Box component={'form'} display={'contents'} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers sx={{ px: 0, py: 4 }}>
          {error ? (
            <Box>
              <Alert severity="warning" sx={{ marginBottom: 2 }} text={error} />
              {storages.map(storage => (
                <Box key={storage.id} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <ShellTextField sx={{ flex: 1 }} label={t('common.name')} value={storage?.name || ''} disabled />
                  <ShellTextField
                    sx={{ flex: 0.25 }}
                    label={t('productEditor.pairingEditor.pairingID.storage')}
                    value={storage?.pairingId}
                    disabled
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField name="storageName" label={t('common.name')} fullWidth maxLength={255} />

              {hasStorageSystemToRestore && (
                <Box pt={{ xs: 3, sm: 2 }} pl={{ xs: 0, ms: 3 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography>{t('productEditor.restoreStorage.message')}</Typography>
                  <TabButtonGroup
                    name="restorePreviousSettings"
                    options={[
                      { label: t('productEditor.restoreStorage.useDefault'), value: useDefaultsOption },
                      { label: t('productEditor.restoreStorage.restorePrevious'), value: restorePreviousOption },
                    ]}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <Actions actions={actions} />
      </Box>
    </FormProvider>
  )
}

const validationSchema = z.object({
  storageName: z.string().nonempty().max(255),
  restorePreviousSettings: z.enum([useDefaultsOption, restorePreviousOption]),
})
